import { createRouter, createWebHistory } from "vue-router";
import { getBaseUrl } from "@/config/runtime";
import store from "@/store";
import {
  decodePaymentsStreamerSlug,
  encodePaymentsStreamerSlug,
} from "@/utils/paymentsPageSlug";

function paymentsRouteSuffix(path) {
  const p = String(path || "");
  if (p.endsWith("/success")) return "/success";
  if (p.endsWith("/failed")) return "/failed";
  return "";
}

function normalizePaymentsRouteLocation(to) {
  const rawSlug = to.params?.streamername;
  if (rawSlug == null || rawSlug === "") return null;
  const decoded = decodePaymentsStreamerSlug(rawSlug);
  const encoded = encodePaymentsStreamerSlug(decoded);
  if (!encoded || !String(to.path || "").startsWith("/payments/")) return null;
  const suffix = paymentsRouteSuffix(to.path);
  const canonicalPath = `/payments/${encoded}${suffix}`;
  if (to.path === canonicalPath) return null;
  return {
    path: canonicalPath,
    query: to.query,
    hash: to.hash,
    replace: true,
  };
}

const routes = [
  {
    path: "/",
    redirect: "/dashboard",
  },
  {
    path: "/auth",
    component: () => import("@/layouts/AuthLayout.vue"),
    children: [
      {
        path: "login",
        name: "login",
        component: () => import("@/pages/auth/LoginPage.vue"),
        meta: { title: "Вход" },
      },
      {
        path: "register",
        name: "register",
        component: () => import("@/pages/auth/RegisterPage.vue"),
        meta: { title: "Регистрация" },
      },
      {
        path: "restore-password",
        name: "restore-password",
        component: () => import("@/pages/auth/RestorePasswordPage.vue"),
        meta: { title: "Восстановление пароля" },
      },
      {
        path: "verify",
        name: "verify",
        component: () => import("@/pages/auth/VerifyCodePage.vue"),
        meta: { title: "Подтверждение" },
      },
      {
        path: "complete/:backend",
        name: "auth-oauth-complete",
        component: () => import("@/pages/auth/OAuthCompletePage.vue"),
        meta: { requiresAuth: false, title: "OAuth" },
      },
    ],
  },
  {
    path: "/dashboard",
    component: () => import("@/layouts/DashboardLayout.vue"),
    meta: { requiresAuth: true },
    children: [
      {
        path: "",
        name: "dashboard",
        component: () => import("@/pages/dashboard-service/DashboardPage.vue"),
        meta: { title: "Дашборд" },
      },
      {
        path: "donation-settings",
        name: "donation-settings",
        component: () => import("@/pages/dashboard-service/DonationSettingsPage.vue"),
        meta: { title: "Настройка страницы донатов" },
      },
      {
        path: "widget-settings",
        redirect: { name: "widget-settings", query: { view: "alerts" } },
      },
      {
        path: "widget-settings/groups",
        name: "widget-settings",
        component: () => import("@/pages/dashboard-service/WidgetSettingsPage.vue"),
        meta: { title: "Настройка виджетов" },
      },
      {
        path: "donation-history",
        name: "donation-history",
        component: () => import("@/pages/dashboard-service/DonationHistoryPage.vue"),
        meta: { title: "История донатов" },
      },
      {
        path: "payments",
        name: "payments",
        component: () => import("@/pages/dashboard-service/PaymentsPage.vue"),
        meta: { title: "Выплаты" },
      },
      {
        path: "goals",
        name: "goals",
        component: () => import("@/pages/dashboard-service/GoalsPage.vue"),
        meta: { title: "Цели" },
      },
      {
        path: "account-settings",
        name: "account-settings",
        component: () => import("@/pages/dashboard-service/AccountSettingsPage.vue"),
        meta: { title: "Настройки аккаунта" },
      },
    ],
  },
  {
    path: "/payments/:streamername/thanks",
    redirect: (to) => {
      const slug = encodePaymentsStreamerSlug(
        decodePaymentsStreamerSlug(to.params.streamername),
      );
      return { path: `/payments/${slug}/success` };
    },
  },
  {
    path: "/payments/:streamername/success",
    name: "payment-success",
    meta: { requiresAuth: false, title: "Спасибо" },
    component: () =>
      import("@/pages/payments-service/PaymentSuccessPage.vue"),
  },
  {
    path: "/payments/:streamername/failed",
    name: "payment-failed",
    meta: { requiresAuth: false, title: "Платёж не выполнен" },
    component: () =>
      import("@/pages/payments-service/PaymentFailedPage.vue"),
  },
  {
    path: "/payments/:streamername",
    meta: { requiresAuth: false },
    component: () => import("@/pages/payments-service/[streamername].vue"),
  },
  {
    path: "/orders/:paymentId/payment",
    name: "crypto-payment-details",
    meta: { requiresAuth: false, title: "Реквизиты оплаты" },
    component: () =>
      import("@/pages/payments-service/CryptoPaymentDetailsPage.vue"),
  },
  {
    path: "/orders/:paymentId",
    name: "crypto-payment-order",
    meta: { requiresAuth: false, title: "Оплата Crypto" },
    component: () =>
      import("@/pages/payments-service/CryptoPaymentOrderPage.vue"),
  },
  {
    path: "/donations",
    name: "widget-donations",
    meta: { requiresAuth: false },
    component: () => import("@/pages/widget/DonationsWidgetPage.vue"),
  },
  {
    path: "/widget/statistic-widget",
    name: "widget-statistic-widget",
    meta: { requiresAuth: false },
    component: () => import("@/pages/widget/StatisticWidgetPage.vue"),
  },
  {
    path: "/widget/top-donators",
    redirect: (to) => ({
      name: "widget-statistic-widget",
      query: to.query,
    }),
  },
  {
    path: "/widget/goal/:campaignId",
    name: "widget-goal",
    meta: { requiresAuth: false },
    component: () => import("@/pages/widget/GoalWidgetPage.vue"),
  },
  {
    path: '/donations/:id',
    meta: { requiresAuth: false },
    redirect: (to) => {
      const id = to.params?.id ? String(to.params.id) : "";
      return id ? `/donations?token=${encodeURIComponent(id)}` : "/donations";
    },
  },
  {
    path: "/widget/last-events",
    name: "widget-last-events",
    meta: { requiresAuth: false, title: "Последние события" },
    component: () => import("@/pages/widget/LastEventsWidgetPage.vue"),
  },
  {
    path: "/widget/video",
    name: "widget-video",
    meta: { requiresAuth: false, title: "Видео" },
    component: () => import("@/pages/widget/VideoWidgetPage.vue"),
  },
  {
    path: "/legal/:slug",
    name: "legal-document",
    meta: { requiresAuth: false, title: "Документ" },
    props: (route) => ({ slug: String(route.params.slug || "").trim() }),
    component: () => import("@/pages/legal/LegalDocumentPage.vue"),
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/",
  },
];

const router = createRouter({
  history: createWebHistory(getBaseUrl()),
  routes,
});

router.beforeEach((to) => {
  const normalizedPaymentsRoute = normalizePaymentsRouteLocation(to);
  if (normalizedPaymentsRoute) {
    return normalizedPaymentsRoute;
  }
  const requiresAuth = to.matched.some((record) => record.meta?.requiresAuth);
  if (!requiresAuth) {
    return true;
  }
  const isAuthenticated = store.getters["auth/isAuthenticated"];
  if (isAuthenticated) {
    return true;
  }
  return {
    name: "login",
    query: { redirect: to.fullPath },
  };
});

router.afterEach(() => {
  store.commit("auth/setError", null);
});

export default router;
